import { useCallback } from "react";

import { ensureValue } from "../../../../services/util";

import { useMaybeCurrentOperationId } from "../currentOperationId";
import { useInvalidateOperation, useClearOperation } from "../operation";

// ###################################
// ## useInvalidateCurrentOperation ##
// ###################################

export type ResultOfUseInvalidateCurrentOperation = () => Promise<void>;
export type UseInvalidateCurrentOperation =
  () => ResultOfUseInvalidateCurrentOperation;

/**
 * Invalidates the current operation data.
 *
 * A fresh fetch of data will be initiated when a hook instance is active that uses this data.
 */
export const useInvalidateCurrentOperation: UseInvalidateCurrentOperation =
  () => {
    const currentOperationId = useMaybeCurrentOperationId();
    const invalidateOperation = useInvalidateOperation();
    return useCallback(async () => {
      await invalidateOperation(
        ensureValue(currentOperationId, "currentOperationId"),
      );
    }, [invalidateOperation, currentOperationId]);
  };

// ##############################
// ## useClearCurrentOperation ##
// ##############################

export type ResultOfUseClearCurrentOperation = () => Promise<void>;
export type UseClearCurrentOperation = () => ResultOfUseClearCurrentOperation;

/**
 * Clears and invalidates the current operation data.
 *
 * A fresh fetch of data will be initiated when a hook instance is active that uses this data.
 */
export const useClearCurrentOperation: UseClearCurrentOperation = () => {
  const clearOperation = useClearOperation();
  const currentOperationId = useMaybeCurrentOperationId();
  return useCallback(async () => {
    await clearOperation(ensureValue(currentOperationId, "currentOperationId"));
  }, [clearOperation, currentOperationId]);
};
